import React, { useRef, useLayoutEffect, useCallback, useMemo, memo } from 'react';
import HigieneOralSimplificadaTable from './HigieneOralSimplificadaTable';
import IndicadoresSaludBucalCols from './IndicadoresSaludBucalCols';
import IndicesCpoCeoTable from './IndicesCpoCeoTable';
import { normalizeHigieneOralSimplificada, syncHosLegacyFields } from '../../data/higieneOralSimplificada';
import { hosTieneDatos, indicadoresColsTieneDatos, cpoCeoTieneDatos } from '../../utils/mspReadHelpers';
import { captureScrollPositions, restoreScrollPositions } from '../../utils/preserveScrollPosition';
import MspReadEmpty from './MspReadEmpty';

function MspIndStackSubBlock({ id, title, icon, tone, readOnly, children }) {
  if (readOnly) {
    return (
      <div className={`msp-read-subblock msp-read-subblock--${tone}`}>
        <h5 className="msp-read-subblock__title">
          <i className={`bi ${icon}`} />
          {title}
        </h5>
        {children}
      </div>
    );
  }

  return (
    <section id={id} className={`msp-ind-stack-block msp-ind-stack-block--${tone}`}>
      <header className="msp-ind-stack-block__hdr">
        <i className={`bi ${icon}`} aria-hidden />
        <h4 className="msp-ind-stack-block__title">{title}</h4>
      </header>
      <div className="msp-ind-stack-block__body">{children}</div>
    </section>
  );
}

const HosSection = memo(function HosSection({ hos, readOnly, onChange, compact }) {
  return (
    <div className={compact ? 'hos-panel--compact' : undefined}>
      <HigieneOralSimplificadaTable
        value={hos}
        onChange={readOnly ? undefined : onChange}
        readOnly={readOnly}
        showSectionTitle={false}
      />
    </div>
  );
});

const ColsSection = memo(function ColsSection({
  enfermedadPeriodontal,
  angleClasificacion,
  fluorosis,
  maloclusion,
  readOnly,
  onChange,
}) {
  return (
    <IndicadoresSaludBucalCols
      enfermedadPeriodontal={enfermedadPeriodontal}
      angleClasificacion={angleClasificacion}
      fluorosis={fluorosis}
      maloclusion={maloclusion}
      onChange={readOnly ? undefined : onChange}
      readOnly={readOnly}
      showSectionTitle={false}
      showMaloclusionNotas={!readOnly || Boolean(maloclusion)}
    />
  );
});

const CpoSection = memo(function CpoSection({ form, readOnly, onChange }) {
  return (
    <IndicesCpoCeoTable
      form={form}
      onChange={readOnly ? undefined : onChange}
      readOnly={readOnly}
      showSectionTitle={false}
    />
  );
});

/**
 * Panel completo de indicadores MSP (H.O.S. + periodontal / Angle / fluorosis + CPO-ceo).
 */
function IndicadoresOdontogramaPanel({
  form,
  setForm,
  readOnly = false,
  compact = false,
  showCpoCeo = true,
}) {
  const scrollSnapRef = useRef(null);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (!scrollSnapRef.current) return;
    const snapshots = scrollSnapRef.current;
    scrollSnapRef.current = null;
    const restore = () => restoreScrollPositions(snapshots);
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(() => {
        restore();
        setTimeout(restore, 0);
      });
    });
  });

  const captureScroll = useCallback(() => {
    const anchor =
      document.activeElement?.closest?.('.msp-ind-panel')
      || document.activeElement?.closest?.('.msp-ind-stack-block')
      || document.activeElement?.closest?.('.indicadores-odonto-panel')
      || document.activeElement
      || rootRef.current;
    scrollSnapRef.current = captureScrollPositions(anchor);
  }, []);

  const setFormKeepScroll = useCallback(
    (updater) => {
      if (!setForm) return;
      captureScroll();
      setForm(updater);
    },
    [setForm, captureScroll],
  );

  const hos = useMemo(() => {
    if (form?.higieneOralSimplificada?.filas?.length) {
      return form.higieneOralSimplificada;
    }
    return normalizeHigieneOralSimplificada(form);
  }, [form?.higieneOralSimplificada]);

  const cpoFormFields = useMemo(
    () => ({
      indiceCPO_C: form.indiceCPO_C,
      indiceCPO_P: form.indiceCPO_P,
      indiceCPO_O: form.indiceCPO_O,
      indiceCPO_Total: form.indiceCPO_Total,
      indiceCeo_c: form.indiceCeo_c,
      indiceCeo_e: form.indiceCeo_e,
      indiceCeo_o: form.indiceCeo_o,
      indiceCeo_Total: form.indiceCeo_Total,
    }),
    [
      form.indiceCPO_C,
      form.indiceCPO_P,
      form.indiceCPO_O,
      form.indiceCPO_Total,
      form.indiceCeo_c,
      form.indiceCeo_e,
      form.indiceCeo_o,
      form.indiceCeo_Total,
    ],
  );

  const handleHosChange = useCallback(
    (nextHos) => {
      const legacy = syncHosLegacyFields(nextHos);
      setFormKeepScroll((f) => ({
        ...f,
        higieneOralSimplificada: nextHos,
        placa: legacy.placa,
        calculo: legacy.calculo,
        gingivitis: legacy.gingivitis,
      }));
    },
    [setFormKeepScroll],
  );

  const handleColsChange = useCallback(
    (patch) => setFormKeepScroll((f) => ({ ...f, ...patch })),
    [setFormKeepScroll],
  );

  const handleCpoChange = useCallback(
    (updater) => {
      setFormKeepScroll((f) => {
        const next = typeof updater === 'function' ? updater(f) : { ...f, ...updater };
        return { ...f, ...next };
      });
    },
    [setFormKeepScroll],
  );

  if (readOnly && !hosTieneDatos(form) && !indicadoresColsTieneDatos(form) && !cpoCeoTieneDatos(form)) {
    return (
      <MspReadEmpty
        icon="bi-bar-chart-line"
        message="No hay indicadores de salud bucal guardados para este paciente."
      />
    );
  }


  return (
    <div
      ref={rootRef}
      className={`indicadores-odonto-panel${compact ? ' indicadores-odonto-panel--compact' : ''}${readOnly ? ' indicadores-odonto-panel--readonly' : ''}`}
    >
      <MspIndStackSubBlock id="msp-ind-hos" title="7 Indicadores de salud bucal — H.O.S." icon="bi-droplet-half" tone="hos" readOnly={readOnly}>
        <HosSection hos={hos} readOnly={readOnly} onChange={handleHosChange} compact={compact} />
      </MspIndStackSubBlock>

      <MspIndStackSubBlock
        id="msp-ind-cols"
        title="Enfermedad periodontal · Mal oclusión · Fluorosis"
        icon="bi-heart-pulse"
        tone="cols"
        readOnly={readOnly}
      >
        <ColsSection
          enfermedadPeriodontal={form.enfermedadPeriodontal}
          angleClasificacion={form.angleClasificacion}
          fluorosis={form.fluorosis}
          maloclusion={form.maloclusion}
          readOnly={readOnly}
          onChange={handleColsChange}
        />
      </MspIndStackSubBlock>

      {showCpoCeo && (
        <MspIndStackSubBlock id="msp-ind-cpo" title="8 Índices CPO-ceo" icon="bi-calculator" tone="cpo" readOnly={readOnly}>
          <CpoSection form={cpoFormFields} readOnly={readOnly} onChange={handleCpoChange} />
        </MspIndStackSubBlock>
      )}
    </div>
  );
}

export default memo(IndicadoresOdontogramaPanel);
